import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";

// ─── helpers ─────────────────────────────────────────────────────────────────

function genWave(n, base, amp, noise = 0.5) {
  return Array.from({ length: n }, (_, i) =>
    +(base + Math.sin(i / 12) * amp + Math.sin(i / 5) * amp * 0.3 + (Math.random() - 0.5) * noise).toFixed(2)
  );
}

function avg(arr) {
  const valid = arr.filter((v) => v !== null && !isNaN(v));
  return valid.length
    ? (valid.reduce((a, b) => a + b, 0) / valid.length).toFixed(2)
    : "—";
}

function buildDemoData(n = 42) {
  const base = new Date("2026-04-22T10:00:00Z");
  const me = genWave(n, 41, 3, 1.5);
  const ae = genWave(n, 76, 4, 2);
  const ab = genWave(n, 96, 2, 1);
  return Array.from({ length: n }, (_, i) => {
    const t = new Date(base.getTime() + i * 600_000);
    const label =
      `${String(t.getUTCHours()).padStart(2,"0")}:${String(t.getUTCMinutes()).padStart(2,"0")} ` +
      `${String(t.getUTCMonth()+1).padStart(2,"0")}/${String(t.getUTCDate()).padStart(2,"0")}`;
    return {
      t: label,
      me: me[i], ae: ae[i], ab: ab[i],
      meCounter: +(11717468 + i * 12).toFixed(2),
      aeCounter: +(3173803  + i * 11).toFixed(2),
      abCounter: +(9338     + i * 0.5).toFixed(2),
    };
  });
}

/**
 * Confirmed API schema:
 * {
 *   timestamp: "2026-03-01T00:00:00+00:00",
 *   me: { flow_rate: number, counter: number },
 *   ae: { flow_rate: number, counter: number },
 *   ab: { flow_rate: number, counter: number }
 * }
 */
function mapApiRecord(d) {
  let label = d.timestamp ?? "";
  try {
    const dt = new Date(d.timestamp);
    label =
      `${String(dt.getUTCHours()).padStart(2,"0")}:${String(dt.getUTCMinutes()).padStart(2,"0")} ` +
      `${String(dt.getUTCMonth()+1).padStart(2,"0")}/${String(dt.getUTCDate()).padStart(2,"0")}`;
  } catch (_) {}
  return {
    t:          label,
    me:         d?.me?.flow_rate ?? null,
    ae:         d?.ae?.flow_rate ?? null,
    ab:         d?.ab?.flow_rate ?? null,
    meCounter:  d?.me?.counter   ?? null,
    aeCounter:  d?.ae?.counter   ?? null,
    abCounter:  d?.ab?.counter   ?? null,
  };
}

// ─── chart primitives ─────────────────────────────────────────────────────────

const TICK  = { fontSize: 9, fill: "#888" };
const TT_CS = { fontSize: 11, background: "#fff", border: "1px solid #dde1e8", borderRadius: 4 };

function MiniChart({ data, dataKey, color, height = 90, unit = "" }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 2, right: 24, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
        <XAxis
          dataKey="t" tick={TICK}
          interval={Math.max(0, Math.floor(data.length / 6) - 1)}
          tickLine={false} axisLine={false}
        />
        <YAxis domain={["auto","auto"]} tick={TICK} tickLine={false} axisLine={false} width={42} />
        <Tooltip
          contentStyle={TT_CS} itemStyle={{ fontSize: 11 }}
          formatter={(v) => [`${v}${unit}`, dataKey.toUpperCase()]}
        />
        <Line
          type="monotone" dataKey={dataKey} stroke={color}
          dot={false} strokeWidth={1.4} isAnimationActive={false} connectNulls={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── VPR ring ─────────────────────────────────────────────────────────────────

function VPRRing({ value = 74.2 }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    const cx = 50, cy = 50, r = 38;
    ctx.clearRect(0, 0, 100, 100);
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, 2 * Math.PI);
    ctx.strokeStyle = "#e8f5e9"; ctx.lineWidth = 9; ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + 2 * Math.PI * (value / 100));
    ctx.strokeStyle = "#43a047"; ctx.lineWidth = 9; ctx.lineCap = "round"; ctx.stroke();
  }, [value]);
  return (
    <div style={{ position:"relative", width:100, height:100 }}>
      <canvas ref={ref} width={100} height={100} />
      <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", textAlign:"center" }}>
        <div style={{ fontSize:18, fontWeight:700, color:"#43a047", lineHeight:1 }}>{value}%</div>
      </div>
    </div>
  );
}

// ─── layout atoms ─────────────────────────────────────────────────────────────

function LegendRow({ items }) {
  return (
    <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginTop:4 }}>
      {items.map(({ color, label }) => (
        <span key={label} style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#555" }}>
          <span style={{ width:16, height:2, background:color, display:"inline-block", borderRadius:1 }} />
          {label}
        </span>
      ))}
    </div>
  );
}

function ChartBlock({ title, subtitle, children }) {
  return (
    <div style={{ background:"#fff", border:"1px solid #dde1e8", borderRadius:4, padding:"10px 14px 8px", marginBottom:8 }}>
      <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:4 }}>
        <span style={{ fontSize:12, fontWeight:600, color:"#333" }}>{title}</span>
        {subtitle && <span style={{ fontSize:11, color:"#888" }}>{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function StatPill({ label, value, color = "#1a2744" }) {
  return (
    <div style={{ background:"#f7f8fa", border:"1px solid #e4e7ed", borderRadius:4, padding:"6px 10px", minWidth:110 }}>
      <div style={{ fontSize:10, color:"#888", marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:13, fontWeight:700, color }}>{value}</div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function PerformanceOverview() {
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");
  const [data,      setData]      = useState(() => buildDemoData(42));
  const [meta,      setMeta]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [usingDemo, setUsingDemo] = useState(true);
  const [activeTab, setActiveTab] = useState("VPR");

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `https://api.hanoch.in/api/fuel-consumption/?start_date=${startDate}&end_date=${endDate}`
      );
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const json = await res.json();

      // Response: { start_date, end_date, interval, count, data: [...] }
      const records = Array.isArray(json) ? json : (json.data ?? []);
      if (!records.length) throw new Error("No records for selected range");

      setData(records.map(mapApiRecord));
      setMeta({ interval: json.interval ?? "—", count: json.count ?? records.length });
      setUsingDemo(false);
    } catch (err) {
      console.error("Fuel API:", err);
      setError(err.message);
      setData([]);
      setMeta(null);
      setUsingDemo(false);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Derived stats — only over non-null values
  const meAvg = avg(data.map((d) => d.me));
  const aeAvg = avg(data.map((d) => d.ae));
  const abAvg = avg(data.map((d) => d.ab));
  const meMax = Math.max(...data.map((d) => d.me ?? -Infinity));
  const aeMax = Math.max(...data.map((d) => d.ae ?? -Infinity));

  const S = {
    root:       { fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", fontSize:13, background:"#f0f2f5", height:"100vh", overflow:"hidden", display:"flex", flexDirection:"column" },
    layout:     { display:"flex", flex:1, overflow:"hidden" },
    center:     { flex:1, display:"flex", flexDirection:"column", overflow:"hidden" },
    filterBar:  { background:"#fff", borderBottom:"1px solid #dde1e8", padding:"6px 14px", display:"flex", alignItems:"center", gap:8, flexShrink:0, flexWrap:"wrap" },
    ftitle:     { fontSize:13, fontWeight:600, color:"#333" },
    lbl:        { fontSize:12, color:"#666" },
    dateInput:  { border:"1px solid #ccc", background:"#fff", padding:"3px 7px", borderRadius:3, fontSize:12, color:"#333" },
    searchBtn:  { background: loading ? "#999" : "#1a2744", color:"#fff", border:"none", padding:"4px 16px", borderRadius:3, fontSize:12, cursor: loading || !startDate || !endDate ? "not-allowed" : "pointer", opacity: !startDate || !endDate ? 0.45 : 1 },
    scroll:     { flex:1, overflowY:"auto", padding:"10px 12px" },
    statsRow:   { display:"flex", gap:8, padding:"8px 12px 0", flexWrap:"wrap" },
    rightPanel: { width:320, background:"#fff", borderLeft:"1px solid #dde1e8", display:"flex", flexDirection:"column", overflow:"hidden", flexShrink:0 },
    tabs:       { display:"flex", borderBottom:"1px solid #dde1e8", background:"#f7f8fa", flexShrink:0 },
    tab:  (a)  => ({ padding:"8px 16px", fontSize:12, fontWeight:600, color: a ? "#1a2744" : "#888", cursor:"pointer", borderBottom: a ? "2px solid #1a2744" : "2px solid transparent" }),
    rpScroll:   { flex:1, overflowY:"auto" },
    sbar:       { display:"flex", height:6, borderRadius:2, overflow:"hidden", margin:"4px 0 2px" },
    badge: (bg, fg, bd) => ({ background:bg, color:fg, border:`1px solid ${bd}`, borderRadius:3, padding:"2px 8px", fontSize:11 }),
  };

  return (
     <React.Fragment>
          <div className="page-content">
    <div style={S.root}>
      <div style={S.layout}>

        {/* CENTER */}
        <div style={S.center}>

          {/* Filter bar */}
          <div style={S.filterBar}>
            <span style={S.ftitle}>Performance Overview</span>
            <span style={{ color:"#ddd" }}>|</span>
            <span style={S.lbl}>Start</span>
            <input type="date" style={S.dateInput} value={startDate}
              onChange={(e) => setStartDate(e.target.value)} />
            <span style={S.lbl}>End</span>
            <input type="date" style={S.dateInput} value={endDate}
              onChange={(e) => setEndDate(e.target.value)} />
            <button style={S.searchBtn}
              disabled={loading || !startDate || !endDate}
              onClick={fetchData}>
              {loading ? "Loading…" : "Search"}
            </button>

            {usingDemo && !error &&
              <span style={S.badge("#fff8e1","#7a5c00","#f9c74f")}>Demo data</span>}
            {error &&
              <span style={S.badge("#fde8e8","#8b1a1a","#f5a5a5")} title={error}>⚠ {error}</span>}
            {meta &&
              <span style={{ fontSize:11, color:"#888" }}>
                {meta.count.toLocaleString()} records · {meta.interval}
              </span>}
          </div>

          {/* Summary pills */}
          <div style={S.statsRow}>
            <StatPill label="ME Avg Flow Rate"  value={meAvg !== "—" ? `${meAvg} L/min` : "—"} color="#2e7d32" />
            <StatPill label="ME Peak Flow Rate" value={isFinite(meMax) ? `${meMax.toFixed(2)} L/min` : "—"} color="#2e7d32" />
            <StatPill label="AE Avg Flow Rate"  value={aeAvg !== "—" ? `${aeAvg} L/min` : "—"} color="#e65100" />
            <StatPill label="AE Peak Flow Rate" value={isFinite(aeMax) ? `${aeMax.toFixed(2)} L/min` : "—"} color="#e65100" />
            <StatPill label="AB Avg Flow Rate"  value={abAvg !== "—" ? `${abAvg} L/min` : "—"} color="#1565c0" />
          </div>

          {/* Charts */}
          <div style={S.scroll}>

            <ChartBlock title="ME — Fuel Flow Rate (L/min)" subtitle={`avg ${meAvg} L/min`}>
              <MiniChart data={data} dataKey="me" color="#2e7d32" height={95} unit=" L/min" />
              <div style={S.sbar}>
                <div style={{ background:"#43a047", flex:3 }} />
                <div style={{ background:"#fbc02d", flex:1 }} />
                <div style={{ background:"#e53935", flex:0.5 }} />
              </div>
              <LegendRow items={[{ color:"#2e7d32", label:"ME Flow Rate" }]} />
            </ChartBlock>

            <ChartBlock title="ME — Cumulative Counter">
              <MiniChart data={data} dataKey="meCounter" color="#81c784" height={70} />
              <LegendRow items={[{ color:"#81c784", label:"ME Counter (total volume)" }]} />
            </ChartBlock>

            <ChartBlock title="AE — Fuel Flow Rate (L/min)" subtitle={`avg ${aeAvg} L/min`}>
              <MiniChart data={data} dataKey="ae" color="#e65100" height={95} unit=" L/min" />
              <LegendRow items={[{ color:"#e65100", label:"AE Flow Rate" }]} />
            </ChartBlock>

            <ChartBlock title="AE — Cumulative Counter">
              <MiniChart data={data} dataKey="aeCounter" color="#ffb74d" height={70} />
              <LegendRow items={[{ color:"#ffb74d", label:"AE Counter (total volume)" }]} />
            </ChartBlock>

            <ChartBlock title="AB — Fuel Flow Rate (L/min)" subtitle={`avg ${abAvg} L/min`}>
              <MiniChart data={data} dataKey="ab" color="#1565c0" height={95} unit=" L/min" />
              <LegendRow items={[{ color:"#1565c0", label:"AB Flow Rate" }]} />
            </ChartBlock>

            <ChartBlock title="AB — Cumulative Counter">
              <MiniChart data={data} dataKey="abCounter" color="#64b5f6" height={70} />
              <LegendRow items={[{ color:"#64b5f6", label:"AB Counter (total volume)" }]} />
            </ChartBlock>

          </div>
        </div>

        {/* RIGHT PANEL */}
        <div style={S.rightPanel}>
          <div style={S.tabs}>
            {["VPR","KPI Details"].map((t) => (
              <div key={t} style={S.tab(activeTab === t)} onClick={() => setActiveTab(t)}>{t}</div>
            ))}
          </div>

          <div style={S.rpScroll}>
            <div style={S.vesselCard = { padding:"12px 14px", borderBottom:"1px solid #eee" }}>
              <div style={{ fontSize:14, fontWeight:700, color:"#1a2744" }}>
                ARK Prestige <span style={{ color:"#29b6f6", fontSize:13 }}>ℹ</span>
              </div>
              <div style={{ fontSize:11, color:"#888", marginTop:1 }}>
                Container Ship | SMC INDIA | IMO : 9302944
              </div>

              <div style={{ margin:"10px 0", borderRadius:4, overflow:"hidden", background:"linear-gradient(135deg,#1a3a6e,#2a5298 40%,#c0392b 80%)", height:150, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg viewBox="0 0 280 100" style={{ width:"80%", opacity:0.85 }}>
                  <path d="M10,65 L270,65 L255,85 L25,85 Z" fill="#c0392b"/>
                  <path d="M10,55 L270,55 L270,65 L10,65 Z" fill="#fff"/>
                  <rect x="80" y="20" width="100" height="35" fill="#ecf0f1" rx="2"/>
                  <rect x="95" y="10" width="70" height="12" fill="#bdc3c7" rx="1"/>
                  <rect x="105" y="4" width="50" height="8" fill="#95a5a6" rx="1"/>
                  <rect x="30" y="25" width="4" height="30" fill="#e74c3c"/>
                  <rect x="15" y="25" width="35" height="3" fill="#e74c3c"/>
                  <rect x="200" y="20" width="4" height="35" fill="#e74c3c"/>
                  <rect x="185" y="20" width="35" height="3" fill="#e74c3c"/>
                  {[92,104,116,128,140,152,164].map(x => (
                    <rect key={x} x={x} y="26" width="8" height="6" fill="#29b6f6" rx="1"/>
                  ))}
                  <path d="M10,67 Q70,72 140,67 Q210,62 270,67" stroke="rgba(255,255,255,0.3)" strokeWidth="1" fill="none"/>
                </svg>
              </div>

              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"10px 0 6px" }}>
                <VPRRing value={74.2} />
                <div style={{ fontSize:10, color:"#888", marginTop:4, textAlign:"center" }}>Vessel Performance Rating</div>
              </div>
            </div>

            {/* Live summary */}
            <div style={{ padding:"12px 14px" }}>
              <div style={{ fontSize:12, fontWeight:600, color:"#333", marginBottom:8 }}>Fuel Summary</div>
              {[
                { label:"ME avg flow rate", value: meAvg !== "—" ? `${meAvg} L/min` : "—", color:"#2e7d32" },
                { label:"AE avg flow rate", value: aeAvg !== "—" ? `${aeAvg} L/min` : "—", color:"#e65100" },
                { label:"AB avg flow rate", value: abAvg !== "—" ? `${abAvg} L/min` : "—", color:"#1565c0" },
                { label:"Data points",      value: meta ? meta.count.toLocaleString() : `${data.length} (demo)`, color:"#555" },
                { label:"Interval",         value: meta?.interval ?? "10 min (demo)", color:"#555" },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 0", borderBottom:"1px solid #f0f2f5" }}>
                  <span style={{ fontSize:11, color:"#888" }}>{label}</span>
                  <span style={{ fontSize:12, fontWeight:600, color }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
    </div>
    </React.Fragment>
  );
}